import { React, useState, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxDatePicker from 'components/custom/AdxDatePicker'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxInput from 'components/custom/AdxInput'
import AdxDropdownWithFilter from 'components/reusable/AdxDropdownWithFilter'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import { ErrorMessage } from 'formik'
import FaceIcon from '@mui/icons-material/Face'
import { _calculateWorkExperienceYear } from 'services/helper/DateTime'

const EditEmploymentTab = ({ formikBag, departmentList, employmentData }) => {
  // State and function for Dropdown With Filter Functions
  const [dropdownFilterDepartmentManagerOptions, setDropdownFilterDepartmentManagerOptions] =
    useState([])

  const { values, handleChange, handleBlur, setFieldValue, errors, touched } = formikBag

  useEffect(() => {
    if (employmentData) {
      setDropdownFilterDepartmentManagerOptions(employmentData.data.all_staff)
    }
  }, [employmentData])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Employment Details */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'gray-line.main',
          p: 2,
          alignItems: 'center',
        }}
      >
        <Typography color="light-gray-text.main" fontWeight={500}>
          Basic employment details
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 2 }}>
        {/* Employment Type */}
        <AdxDropdown
          placeholder="Employment type"
          label="Employment type"
          sxLabel={{
            color: 'dark-text.main' /*, fontSize: "30px!important" */,
          }}
          sxDropdown={{
            color: 'light-gray-text.main',
            backgroundColor: 'white.main',
          }}
          name="employment_type"
          value={values.employment_type}
          onChange={handleChange}
          onBlur={handleBlur}
          errorMessage={<ErrorMessage name="employment_type" component={AdxErrorMessage} />}
          isErrorShown={touched.employment_type && errors.employment_type}
        >
          <AdxMenuItem value={1} selectedvalue={values.employment_type}>
            {'Full time'}
          </AdxMenuItem>
          <AdxMenuItem value={2} selectedvalue={values.employment_type}>
            {'Intern'}
          </AdxMenuItem>
          <AdxMenuItem value={3} selectedvalue={values.employment_type}>
            {'Contract'}
          </AdxMenuItem>
        </AdxDropdown>

        <Box gap={2} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} width="100%">
          <Box width={{ xs: '100%', sm: '40%' }}>
            <AdxDatePicker
              label="Hiring date"
              name="hiring_date"
              value={values.hiring_date}
              onChange={(e) => setFieldValue(`hiring_date`, e)}
              onBlur={handleBlur}
              disableFuture
              errorMessage={<ErrorMessage name="hiring_date" component={AdxErrorMessage} />}
              isErrorShown={touched.hiring_date && errors.hiring_date}
            />
          </Box>
          <Box
            width={{ xs: '100%', sm: '60%' }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AdxDatePicker
              label="Commencement date"
              name="commencement_date"
              disableFuture
              sx={{ width: 3 / 4, mr: 1 }}
              onChange={(e) => setFieldValue(`commencement_date`, e)}
              onBlur={handleBlur}
              value={values.commencement_date}
              errorMessage={<ErrorMessage name="commencement_date" component={AdxErrorMessage} />}
              isErrorShown={touched.commencement_date && errors.commencement_date}
            />
            <Typography sx={{ width: 1 / 4, pt: 3 }} fontWeight={400} color="light-gray-text.main">
              {_calculateWorkExperienceYear(values.commencement_date)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Job Information */}
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'gray-line.main',
            p: 2,
            alignItems: 'center',
          }}
        >
          <Typography color="light-gray-text.main" fontWeight={500}>
            Job information
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              width: '100%',
              gap: 2,
            }}
          >
            <AdxInput
              label="Employee ID"
              sxLabel={{
                color: 'dark-text.main' /*, fontSize: "30px!important" */,
              }}
              sxInput={{
                color: 'light-gray-text.main',
                backgroundColor: 'white.main',
              }}
              size="standard"
              placeholder="Employee ID"
              name="internal_employee_id"
              value={values.internal_employee_id}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={
                <ErrorMessage name="internal_employee_id" component={AdxErrorMessage} />
              }
              isErrorShown={touched.internal_employee_id && errors.internal_employee_id}
            />
            <AdxInput
              label="Work Email"
              sxLabel={{
                color: 'dark-text.main' /*, fontSize: "30px!important" */,
              }}
              sxInput={{
                color: 'light-gray-text.main',
                backgroundColor: 'white.main',
              }}
              size="standard"
              placeholder="Work Email"
              value={employmentData && employmentData.data.work_email}
              disabled
            />
          </Box>
          <Box>
            <AdxInput
              label="Job designation"
              sxLabel={{
                color: 'dark-text.main' /*, fontSize: "30px!important" */,
              }}
              sxInput={{
                color: 'light-gray-text.main',
                backgroundColor: 'white.main',
              }}
              size="standard"
              placeholder="Job designation"
              name="designation"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.designation}
              errorMessage={<ErrorMessage name="designation" component={AdxErrorMessage} />}
              isErrorShown={touched.designation && errors.designation}
            />
          </Box>
          <Box>
            <AdxInput
              label="Job description"
              sxLabel={{
                color: 'dark-text.main' /*, fontSize: "30px!important" */,
              }}
              sxInput={{
                color: 'light-gray-text.main',
                backgroundColor: 'white.main',
              }}
              size="standard"
              placeholder="Job description"
              name="job_description"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.job_description}
              multiline
              minRows={3}
              errorMessage={<ErrorMessage name="job_description" component={AdxErrorMessage} />}
              isErrorShown={touched.job_description && errors.job_description}
            />
          </Box>

          {/* Department */}
          <AdxDropdown
            placeholder="Department"
            label="Department"
            sx={{ width: { xs: '100%', lg: '58%' } }}
            sxLabel={{
              color: 'dark-text.main' /*, fontSize: "30px!important" */,
            }}
            sxDropdown={{
              color: 'light-gray-text.main',
              backgroundColor: 'white.main',
            }}
            name="department_id"
            value={values.department_id}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={<ErrorMessage name="department_id" component={AdxErrorMessage} />}
            isErrorShown={touched.department_id && errors.department_id}
          >
            {departmentList &&
              departmentList?.map((item) => (
                <AdxMenuItem value={item.id} selectedvalue={values.department_id}>
                  {item.name}
                </AdxMenuItem>
              ))}
          </AdxDropdown>
          {/* Reporting Manager */}
          <AdxDropdownWithFilter
            label="Reporting Manager"
            allOptions={employmentData ? employmentData?.data?.all_staff : []}
            setOptions={setDropdownFilterDepartmentManagerOptions}
            filters={['name', 'designation', 'department_name']}
            sx={{
              width: '100%',
              backgroundColor: 'white.main',
            }}
            name="reporting_to_id"
            value={values.reporting_to_id}
            onChange={handleChange}
            onBlur={handleBlur}
            renderValue={(option) => {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {option.profile_image_url ? (
                    <Avatar
                      src={option.profile_image_url}
                      sx={{
                        backgroundColor: 'gray-background.main',
                        width: '40px',
                        height: '40px',
                        mx: 1,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        backgroundColor: 'gray-background.main',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        mx: 1,
                      }}
                    >
                      <FaceIcon fontSize="20px" color="alternate" />
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption">{option.designation}</Typography>
                  </Box>
                </Box>
              )
            }}
          >
            {dropdownFilterDepartmentManagerOptions.map((item) => (
              <AdxMenuItem value={item} selectedvalue={values.reporting_to_id}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {item.profile_image_url ? (
                    <Avatar
                      src={item.profile_image_url}
                      sx={{
                        backgroundColor: 'gray-background.main',
                        width: '40px',
                        height: '40px',
                        mx: 1,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        backgroundColor: 'gray-background.main',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        mx: 1,
                      }}
                    >
                      <FaceIcon fontSize="20px" color="alternate" />
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography variant="body1">{item.name}</Typography>
                    <Typography variant="caption">{item.designation}</Typography>
                  </Box>
                </Box>
              </AdxMenuItem>
            ))}
          </AdxDropdownWithFilter>
          {touched.reporting_to_id && errors.reporting_to_id && (
            <ErrorMessage name="reporting_to_id" component={AdxErrorMessage} />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default EditEmploymentTab
